import { supabase } from '../supabaseClient';
import { throttleSupabaseQuery } from '../../utils/requestThrottler';

export class LeadService {
  // WIZARD SUBMISSIONS MANAGEMENT
  static async getWizardSubmissions() {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching wizard submissions:', error);
      throw error;
    }
  }

  static async getWizardSubmission(id) {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching wizard submission:', error);
      throw error;
    }
  }

  static async updateWizardSubmissionStatus(id, status, adminNotes = null) {
    try {
      // Validate status against allowed values (must match database constraint)
      const validStatuses = ['submitted', 'reviewed', 'contacted', 'converted', 'closed'];

      if (!validStatuses.includes(status)) {
        console.error(`Invalid status "${status}". Valid statuses are: ${validStatuses.join(', ')}`);
        throw new Error(`Invalid status value. Valid statuses are: ${validStatuses.join(', ')}`);
      }

      const updateData = { status };
      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      const { data, error } = await supabase
        .from('wizard_submissions')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating wizard submission:', error);
      throw error;
    }
  }

  static async upsertLeadRanking(id, { score, priority }) {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .update({ lead_score: score, lead_priority: priority })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving lead ranking:', error);
      throw error;
    }
  }

  static async assignWizardSubmission(id, assignedTo) {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .update({ assigned_to: assignedTo })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (data?.assigned_to) {
        let staffId = null;
        try {
          const { data: staffMatch } = await supabase
            .from('staff_profiles')
            .select('id')
            .eq('email', data.assigned_to)
            .single();
          staffId = staffMatch?.id || null;
        } catch (_) {
          staffId = null;
        }

        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: staffId,
          p_event: 'lead_assigned',
          p_metadata: {
            submission_id: data.id,
            assigned_to: data.assigned_to,
            source: 'admin_leads_manager'
          }
        }).catch(() => { });
      }

      return data;
    } catch (error) {
      console.error('Error assigning wizard submission:', error);
      throw error;
    }
  }

  static async autoAssignStaff({ role = null, team = null } = {}) {
    try {
      const { data, error } = await supabase.rpc('admin_auto_assign_staff', {
        p_role: role,
        p_team: team
      });
      if (error) throw error;
      return data?.staff || null;
    } catch (error) {
      console.error('Error auto assigning staff:', error);
      throw error;
    }
  }

  static async deleteWizardSubmission(id) {
    try {
      const { error } = await supabase
        .from('wizard_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting wizard submission:', error);
      throw error;
    }
  }

  // Server-side paginated fetch of wizard submissions
  static async getWizardSubmissionsPaged({
    page = 1,
    pageSize = 20,
    status,
    assignedTo,
    dateFrom,
    dateTo,
    search,
    sortBy = 'created_at', // created_at | updated_at | lead_score | lead_priority | status | assigned_to
    sortDir = 'desc'
  } = {}) {
    try {
      const from = Math.max(0, (page - 1) * pageSize);
      const to = from + pageSize - 1;
      let query = supabase
        .from('wizard_submissions')
        .select('*', { count: 'exact' });

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }
      if (assignedTo && assignedTo.trim()) {
        // Use ilike to allow partial match on assigned_to identifier/email
        query = query.ilike('assigned_to', `%${assignedTo.trim()}%`);
      }
      if (dateFrom) {
        query = query.gte('created_at', dateFrom);
      }
      if (dateTo) {
        query = query.lte('created_at', dateTo);
      }
      if (search && search.trim()) {
        const term = `%${search.trim()}%`;
        const or = [
          `ilike(email,${term})`,
          `ilike(phone,${term})`,
          `ilike(first_name,${term})`,
          `ilike(last_name,${term})`,
          `ilike(selected_pathway,${term})`,
          `ilike(personal_info->>fullName,${term})`,
          `ilike(personal_info->>name,${term})`,
          `ilike(personal_info->>firstName,${term})`,
          `ilike(personal_info->>lastName,${term})`,
          `ilike(country_selection->>country,${term})`
        ].join(',');
        query = query.or(or);
      }

      const allowedSort = new Set(['created_at', 'updated_at', 'lead_score', 'lead_priority', 'status', 'assigned_to']);
      const sortField = allowedSort.has(sortBy) ? sortBy : 'created_at';
      query = query.order(sortField, { ascending: String(sortDir).toLowerCase() === 'asc' });

      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data || [], count: count || 0 };
    } catch (error) {
      console.error('Error fetching paginated wizard submissions:', error);
      return { rows: [], count: 0 };
    }
  }

  // LEAD ACTIVITIES MANAGEMENT
  static async getLeadActivities(submissionId) {
    try {
      const { data, error } = await supabase
        .from('lead_activities')
        .select('*')
        .eq('submission_id', submissionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching lead activities:', error);
      return [];
    }
  }

  static async createLeadActivity(activityData) {
    try {
      const { data, error } = await supabase
        .from('lead_activities')
        .insert([{
          submission_id: activityData.submissionId,
          activity_type: activityData.type,
          title: activityData.title,
          description: activityData.description,
          outcome: activityData.outcome,
          next_action: activityData.nextAction,
          next_action_date: activityData.nextActionDate,
          assigned_to: activityData.assignedTo,
          priority: activityData.priority || 'medium',
          status: activityData.status || 'pending',
          metadata: activityData.metadata || {},
          created_by: activityData.createdBy
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating lead activity:', error);
      throw error;
    }
  }

  static async updateLeadActivity(id, updates) {
    try {
      const { data, error } = await supabase
        .from('lead_activities')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating lead activity:', error);
      throw error;
    }
  }

  static async deleteLeadActivity(id) {
    try {
      const { error } = await supabase
        .from('lead_activities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting lead activity:', error);
      throw error;
    }
  }

  // COMMUNICATION HISTORY MANAGEMENT
  static async getCommunicationHistory(submissionId) {
    try {
      const { data, error } = await supabase
        .from('communication_history')
        .select('*')
        .eq('submission_id', submissionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching communication history:', error);
      return [];
    }
  }

  static async createCommunication(commData) {
    try {
      const { data, error } = await supabase
        .from('communication_history')
        .insert([{
          submission_id: commData.submissionId,
          comm_type: commData.type,
          direction: commData.direction,
          subject: commData.subject,
          summary: commData.summary,
          full_content: commData.fullContent,
          duration_minutes: commData.durationMinutes,
          attachments: commData.attachments || [],
          outcome: commData.outcome,
          sentiment: commData.sentiment,
          created_by: commData.createdBy,
          metadata: commData.metadata || {}
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating communication:', error);
      throw error;
    }
  }

  static async getSubmissionStats(days = 90) {
    try {
      const { data, error } = await supabase.rpc('admin_get_submission_stats', { p_days: days });
      if (error) throw error;
      const payload = Array.isArray(data) ? data[0] : data;
      return {
        totalSubmissions: payload?.total_submissions || 0,
        statusDistribution: payload?.status_distribution || {},
        pathwayDistribution: payload?.pathway_distribution || {},
        leadSources: payload?.lead_sources || {},
        conversionMetrics: payload?.conversion_metrics || {},
        recentSubmissions: payload?.recent_submissions || []
      };
    } catch (error) {
      console.error('Error fetching submission stats:', error);
      return {
        totalSubmissions: 0,
        statusDistribution: {},
        pathwayDistribution: {},
        leadSources: {},
        conversionMetrics: {},
        recentSubmissions: []
      };
    }
  }

  // Throttled version of getWizardSubmissions
  static async getWizardSubmissionsThrottled() {
    return throttleSupabaseQuery(
      async () => {
        const { data, error } = await supabase
          .from('wizard_submissions')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
      },
      'get_wizard_submissions',
      {
        retries: 2,
        useCache: false // Don't cache submissions
      }
    );
  }
}
